import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useLogto } from '@logto/react';
import { useLocation, useNavigate } from 'react-router-dom';

import { env } from '@/env';
import { getRedirectUri, isTelegramWebView, startTelegramSignIn } from '@/lib/logto';
import {
  dimenticaSceltaOspite,
  haSessione,
  prendiRitorno,
  ricordaSceltaOspite,
  salvaRitorno,
  scartaRitorno,
  sceltaOspiteRicordata,
  type Archivio,
} from '@/lib/ospite';
import { peekPendingStartParam } from '@/lib/startParam';
import { useAppSelector } from '@/store/hooks';

/**
 * `true` = questa persona sta guardando l'app da OSPITE (interruttore acceso, nessuna sessione).
 *
 * Stesso predicato della guardia di rete (`haSessione`, bridge O SDK): schermata e rete non
 * possono dare due risposte diverse sulla stessa persona. `AppShell` usa QUESTO hook.
 * A interruttore spento e' sempre `false`, e ogni ramo «ospite» dell'app e' codice morto.
 */
export function useOspite(): boolean {
  // L'interruttore sta DENTRO il selettore: spento, lo stato di auth non si legge nemmeno.
  return useAppSelector((s) => env.featureGuestEnabled && !haSessione(s));
}

/**
 * Il gesto che chiede il login: ricorda la schermata, poi apre il login.
 *
 * ⚠️ Si ricorda la SCHERMATA, non il gesto. Dopo il login si torna li' e la persona ritocca:
 * un «Iscriviti» rieseguito da solo sarebbe una scrittura che nessuno ha confermato.
 *
 * Il login e' lo stesso di `useLogtoAuth().signIn` (bridge nel browser di sistema dentro
 * Telegram, redirect dell'SDK fuori), chiamato qui direttamente: `useLogtoAuth` monta i suoi
 * ascoltatori a ogni istanza, e questo hook sta su ogni pannello «Accedi».
 */
export function useRichiedeAccesso(): (percorso?: string) => void {
  const location = useLocation();
  const { signIn } = useLogto();
  return useCallback(
    (percorso?: string) => {
      if (env.featureGuestEnabled) {
        salvaRitorno(archivio(), percorso ?? `${location.pathname}${location.search}`, Date.now());
      }
      if (isTelegramWebView()) void startTelegramSignIn();
      else void signIn(getRedirectUri());
    },
    [location.pathname, location.search, signIn],
  );
}

/**
 * L'altra meta': appena l'app e' USABILE dopo il login, si torna dove l'ospite era.
 *
 * «Usabile» e' la stessa condizione di `usePendingInvite` (sessione + onboarding chiuso): un
 * utente nuovo passa prima dal wizard, e portarlo altrove a meta' lo interromperebbe.
 * Se c'e' un invito o una sfida in attesa vince quel payload: e' piu' preciso e ha una scadenza propria.
 */
export function useRitornoDopoAccesso(pronta: boolean): void {
  const navigate = useNavigate();
  useEffect(() => {
    if (!env.featureGuestEnabled || !pronta) return;
    if (peekPendingStartParam()) {
      scartaRitorno(archivio());
      return;
    }
    // Si consuma alla prima lettura: se `navigate` cambia e l'effetto rigira, non trova piu' niente.
    const percorso = prendiRitorno(archivio(), Date.now());
    if (percorso) navigate(percorso, { replace: true });
  }, [pronta, navigate]);
}

/** `localStorage`, o un archivio vuoto dove leggerlo lancia (storage bloccato): si torna sulla Hub. */
function archivio(): Archivio {
  try {
    return window.localStorage;
  } catch {
    return { getItem: () => null, setItem: () => undefined, removeItem: () => undefined };
  }
}

// ═══ LA SCELTA «ENTRA COME OSPITE» (il benvenuto, C1 di Luca) ═══
// Uno store minuscolo sopra `localStorage`: `AppShell` (che decide se mostrare il benvenuto) e il
// Profilo ospite (che lo fa rivedere) devono vedere lo STESSO valore nello stesso istante, e
// `localStorage` da solo non avvisa nessuno. Il ripiego in memoria c'e' perche' uno storage
// bloccato non deve chiudere la persona sul benvenuto: la scelta vale almeno per questa apertura.
const ascoltatori = new Set<() => void>();
let sceltoInMemoria = false;
const avvisa = () => ascoltatori.forEach((f) => f());
const iscrivi = (f: () => void) => {
  ascoltatori.add(f);
  return () => {
    ascoltatori.delete(f);
  };
};
const leggiScelta = () => sceltoInMemoria || sceltaOspiteRicordata(archivio());

export function useSceltaOspite(): { scelto: boolean; entra: () => void; rivedi: () => void } {
  const scelto = useSyncExternalStore(iscrivi, leggiScelta, () => false);
  const entra = useCallback(() => {
    sceltoInMemoria = true;
    ricordaSceltaOspite(archivio());
    avvisa();
  }, []);
  const rivedi = useCallback(() => {
    sceltoInMemoria = false;
    dimenticaSceltaOspite(archivio());
    avvisa();
  }, []);
  return { scelto, entra, rivedi };
}
